import { ArrowRight, CalendarPlus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  getDaysUntilInterview,
  getUrgencyLevel,
  getUrgencyMessage,
} from '@/lib/api/profile';

interface InterviewCountdownProps {
  interviewDate: string | null;
}

const urgencyColorMap = {
  urgent: 'text-error',
  soon: 'text-warning',
  upcoming: 'text-primary',
  comfortable: 'text-success',
} as const;

const urgencyBgMap = {
  urgent: 'bg-error/10',
  soon: 'bg-warning/10',
  upcoming: 'bg-primary-light',
  comfortable: 'bg-success/10',
} as const;

export default function InterviewCountdown({
  interviewDate,
}: InterviewCountdownProps) {
  const navigate = useNavigate();

  if (!interviewDate) {
    return (
      <div className="bg-card rounded-2xl p-5 border border-border">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-lg bg-primary-light flex items-center justify-center shrink-0">
            <CalendarPlus size={18} className="text-primary" />
          </div>
          <p className="font-display font-semibold text-base text-text-primary">
            When is your interview?
          </p>
        </div>
        <p className="font-sans text-sm text-text-secondary leading-relaxed mb-4">
          Add your date so we can tailor your practice.
        </p>
        <button
          onClick={() => navigate('/settings')}
          className="group inline-flex items-center gap-1.5 font-sans font-medium text-sm text-primary hover:text-primary-dark transition-colors cursor-pointer"
        >
          Set interview date
          <ArrowRight
            size={14}
            className="transition-transform group-hover:translate-x-0.5"
          />
        </button>
      </div>
    );
  }

  const daysUntil = getDaysUntilInterview(interviewDate);
  const urgency = getUrgencyLevel(daysUntil);
  const message = getUrgencyMessage(urgency);

  const colorClass = urgency ? urgencyColorMap[urgency] : 'text-text-primary';
  const bgClass = urgency ? urgencyBgMap[urgency] : 'bg-card';

  return (
    <div className={`${bgClass} rounded-2xl p-5 border border-border`}>
      <p className="text-text-muted font-sans text-sm mb-1">
        Interview in
      </p>
      <p className={`font-display font-semibold text-3xl ${colorClass}`}>
        {daysUntil !== null && daysUntil >= 0
          ? `${daysUntil} day${daysUntil !== 1 ? 's' : ''}`
          : 'Date passed'}
      </p>
      {message && (
        <p className="text-text-secondary font-sans text-sm mt-2">
          {message}
        </p>
      )}
    </div>
  );
}
